import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useReactToPrint } from 'react-to-print';
import { useMediaQuery } from 'react-responsive';
import Row from './Row/Row';
import Pagination from 'common/Pagination/Pagination';
import styles from './TransactionHistory.module.scss';
import { getTransactionHistory } from 'features/content/services';
import SvgIcon from 'components/Icon/Icon';
import TableLoader from './TableLoader/TableLoader';
import Filters from './Filters/Filters';
import { getColumns } from 'common/helpers/transactionHistory';
import { SvgIcons } from 'common/consts/svgIcons';
import RESPONSIVE from 'common/consts/responsive';
import http from 'app/http';
import PrintTransactionHistory from './PrintTransactionHistory/PrintTransactionHistory';

export const FIRST_PAGE = 1;
export const ITEMS_PER_PAGE = 10;

const preparePrintUrl = (
  url: string,
  dateFrom: string | null,
  dateTo: string | null,
  columnName: string = '',
  sortType: string = '',
) => {
  return (
    url +
    '?fromDate=' +
    dateFrom +
    '&toDate=' +
    dateTo +
    '&sortColumn=' +
    columnName +
    '&sortType=' +
    sortType
  );
};

const TransactionHistory = (props: any) => {
  const { Model, PageData } = props;
  const [isMobile, setIsMobile] = useState<boolean>(
    useMediaQuery({ maxWidth: RESPONSIVE.MOBILE_BREAKPOINT }),
  );
  // Define and initialize local state with default values
  const [transactionsToPrint, setTransactionsToPrint] =
    useState<any>(undefined);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [current, setCurrent] = useState<number>(FIRST_PAGE);
  const [rowCount, setRowCount] = useState<number>(ITEMS_PER_PAGE);
  const [total, setTotal] = useState<number>(0);
  const [rows, setRows] = useState<any[]>([]);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<any>({});
  const [sortBy, setSortBy] = useState<string>('date');
  const [isDesc, setIsDesc] = useState<boolean>(true); // On default orderBy is set to
  const columns = useMemo(() => getColumns(props), [props]);
  // Get number of columns
  const numberOfColumns = (columns || []).reduce((acc: number, curr: any) => {
    if (curr.isVisible) {
      return acc + 1;
    }
    return acc;
  }, 0);
  // Calculate number of pages
  const numberOfPages = useMemo(() => {
    return total > 0 && rowCount > 0 ? Math.ceil(total / rowCount) : 0;
  }, [total, rowCount]);
  const printComponentRef = useRef(null);

  const fetchTransactionHistory = useCallback(async () => {
    // Display loader when start fetching data
    setShowLoader(true);
    // TODO: Confirm is that is necessary to send data as FormData
    const filters = new FormData();
    filters.append('current', (current || FIRST_PAGE).toString());
    filters.append('rowCount', (rowCount || ITEMS_PER_PAGE).toString());
    filters.append('dateFrom', dateFrom || '');
    filters.append('dateTo', dateTo || '');
    filters.append(`sort[${sortBy}]`, isDesc ? 'DESC' : 'ASC');
    filters.append('searchPhrase', '');
    try {
      // Getting data from API
      const response = await getTransactionHistory(
        Model?.DataEndpoint,
        filters,
      );
      if (response.data) {
        setCurrent(response.data.current);
        setRowCount(response.data.rowCount);
        setTotal(response.data.total);
        setRows(response.data.rows);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Hide loader
      setShowLoader(false);
    }
  }, [current, dateRange, sortBy, rowCount, isDesc]);

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });

  const onPrintHistory = useCallback(async () => {
    const sortType = isDesc ? 'DESC' : 'ASC';
    const printUrl = preparePrintUrl(
      Model?.PrintUrl,
      dateFrom,
      dateTo,
      sortBy,
      sortType,
    );
    // window.open(printUrl, "_blank", "noopener,status=0,toolbar=0");
    const response = await http.get(printUrl);
    if (response.data) {
      setTransactionsToPrint(response?.data?.Model?.DataGridResult?.Rows);
      handlePrint();
    }
    return false;
  }, [Model?.PrintUrl, dateFrom, dateTo, sortBy, isDesc]);

  useEffect(() => {
    try {
      fetchTransactionHistory();
    } catch (err) {
      console.error(err);
    }
  }, [fetchTransactionHistory]);

  const onApplyDates = () => {
    setDateRange({ dateTo, dateFrom });
  };

  const onClearDates = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const onSort = (key: string) => {
    if (key === sortBy) {
      setIsDesc(!isDesc);
    } else {
      setSortBy(key);
    }
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };

  useEffect(() => {
    if (isMobile) {
      setShowFilters(false);
    } else {
      setShowFilters(true);
    }
  }, [isMobile]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  return (
    <div
      className={classNames(
        styles.TransactionHistory,
        numberOfPages === 0 && styles.noPagination,
      )}
    >
      <div className={styles.header}>
        <div className={styles.ctaWrapper}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={classNames(styles.ctaBtn, styles.toggleFilterBtn)}
          >
            <SvgIcon
              type={SvgIcons.CALENDAR}
              width={24}
              height={24}
              className={styles.ctaIcon}
            />{' '}
            {PageData?.SearchDateLabel || 'Search date'}
          </button>
          {/* <button onClick={() => null}>
            <SvgIcon type={SvgIcons.PDF} width={24} height={24} /> {PageData?.DownloadLabel}
          </button> */}
          <button onClick={() => onPrintHistory()} className={styles.ctaBtn}>
            <SvgIcon
              type={SvgIcons.PRINTER}
              width={24}
              height={24}
              className={styles.ctaIcon}
            />{' '}
            {PageData?.PrintLabel}
          </button>
        </div>
        {showFilters && (
          <Filters
            dateFrom={dateFrom}
            dateTo={dateTo}
            labelTo={PageData?.DatefilterToLabel}
            placeholderFrom={PageData?.DatefilterfromPlaceholderText}
            placeholderTo={PageData?.DatefiltertoPlaceholderText}
            submitBtnText={PageData?.DatefilterButtonLabel}
            resetBtnText={PageData?.DatefilterClearDatesLabel}
            onChangeDateFrom={(date: string) => setDateFrom(date)}
            onChangeDateTo={(date: string) => setDateTo(date)}
            onSubmit={onApplyDates}
            onReset={onClearDates}
            onClose={() => setShowFilters(!showFilters)}
          />
        )}
      </div>
      {Model?.IsSecuredAccount && (
        <div className={styles.SecuredInfo}>
          {PageData?.SecuredInformationText}
        </div>
      )}
      <div className={styles.table}>
        <div
          className={classNames(styles.row, styles.headRow)}
          style={{ gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)` }}
        >
          {(columns || []).map(
            (column) =>
              column.isVisible && (
                <div key={column.key} className={styles.labelWrapper}>
                  <button
                    className={classNames(
                      styles.label,
                      column.isSortable &&
                        column.key === sortBy &&
                        styles.active, // Display chevron
                      !isDesc && styles.asc, // Rotate chevron
                    )}
                    onClick={() => column.isSortable && onSort(column.key)}
                  >
                    {column.value}
                  </button>
                </div>
              ),
          )}
        </div>
        {showLoader && <TableLoader columnCount={numberOfColumns} />}
        {!showLoader && (rows || []).length > 0 && (
          <div className={styles.content}>
            {(rows || []).map((row, index) => (
              <Row
                key={index}
                data={row}
                columns={columns}
                columnCount={numberOfColumns}
                className={styles.row}
              />
            ))}
          </div>
        )}
        {!showLoader && (rows || []).length === 0 && (
          <div className={styles.noContent}>
            {PageData?.TransactionHistoryTableNoResults}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Pagination
          currentPage={current}
          numberOfPages={numberOfPages}
          numberOfTotalTransactions={total}
          resultsText={PageData?.TransactionHistoryTablePageInfo}
          onChange={(index) => setCurrent(index)}
          rowCount={ITEMS_PER_PAGE}
        />
        {PageData?.TransactionHistoryInformationText && (
          <div className={styles.transactionNotice}>
            {PageData?.TransactionHistoryInformationText}
          </div>
        )}
      </div>
      {transactionsToPrint && (
        <div style={{ display: 'none' }}>
          <PrintTransactionHistory
            ref={printComponentRef}
            Model={Model}
            PageData={PageData}
            columns={columns}
            transactions={transactionsToPrint}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
